import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { PrismButton, PrismInput } from '@mylighthouse/prism-ember';
// @ts-expect-error: Incorrect type
import preventDefault from 'ember-event-helpers/helpers/prevent-default';
import { not } from 'ember-truth-helpers';
import GeneralButtonsButtonSort from 'lumen/components/general/buttons/button-sort';
import HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBase, {
  type HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-title-base';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import {
  HOTEL_SORT_BY,
  type HotelSortBy,
} from 'lumen/utils/constants/ri-constant-values';

export interface HotelAndMarketRevenueInsightHotelBlockHotelBlockTitleSignature extends HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature {
  Args: HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature['Args'] & {
    setSortHotelsAscending: (ascending: boolean) => void;
    setSortHotelsBy: (sortBy: HotelSortBy) => void;
    sortHotelsAscending: boolean;
    sortHotelsBy: HotelSortBy;
  };
}

// Remove the wrapping div so the child content is a direct child of the prism-card and can use the slots
export default class HotelAndMarketRevenueInsightHotelBlockHotelBlockTitle extends HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBase<HotelAndMarketRevenueInsightHotelBlockHotelBlockTitleSignature> {
  @service declare intl: Services['intl'];

  keyForChange = HOTEL_SORT_BY.CHANGE;
  keyForKpi = HOTEL_SORT_BY.KPI;

  get isSortedByChange(): boolean {
    return this.args.sortHotelsBy === HOTEL_SORT_BY.CHANGE;
  }

  get kpiString(): string {
    return this.intl.t(`revenue_insight.${this.riSelections.kpi}`);
  }

  get versusString(): string {
    if (this.riSelections.isBudgetActive) {
      return this.riSelections.vsBudgetText;
    }
    if (this.riSelections.isForecastActive) {
      return this.riSelections.vsForecastText;
    }
    return `${this.intl.t('general.versus.option')} ${this.intl.t('general.last_year')}`;
  }

  @action
  updateSort(sortBy: HotelSortBy): void {
    if (sortBy === this.args.sortHotelsBy) {
      // If the sortBy doesn't change: toggle the order
      this.args.setSortHotelsAscending(!this.args.sortHotelsAscending);
    } else {
      // If the sortBy does change: update it and set the order to descending by default
      this.args.setSortHotelsBy(sortBy);
      this.args.setSortHotelsAscending(false);
    }
  }

  <template>
    <div class="card-title-header card-header">
      <div
        class="title-box block-list-title-container flex items-center justify-between
          {{if this.isSearchActive 'title-box--search-active'}}"
      >
        {{#if this.isLoading}}
          <HotelAndMarketRevenueInsightLoadingStateTitle />
        {{else if this.isSearchActive}}
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{this.searchPlaceholder}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler this.disableSearch}}
          />
        {{else}}
          <div class="subscription-title-column"></div>
          <div class="sortable-column flex items-center justify-between">
            <button
              class="button--unstyled current-value
                {{if this.riSelections.isTargetActive 'current-value--target'}}
                flex items-center justify-between"
              type="button"
              {{on
                "click"
                (preventDefault (fn this.updateSort this.keyForKpi))
              }}
            >
              <span class="truncate">{{this.kpiString}}</span>
              <GeneralButtonsButtonSort
                @isAscending={{@sortHotelsAscending}}
                @isSorted={{not this.isSortedByChange}}
              />
            </button>
            <button
              class="button--unstyled reference-value
                {{if
                  this.riSelections.isTargetActive
                  'reference-value--target'
                }}
                reference-value--row"
              type="button"
              {{on
                "click"
                (preventDefault (fn this.updateSort this.keyForChange))
              }}
            >
              <span class="truncate">{{this.vsText}}</span>
              <GeneralButtonsButtonSort
                @isAscending={{@sortHotelsAscending}}
                @isSorted={{this.isSortedByChange}}
              />
            </button>
          </div>
          <div class="icon-column ml-200 flex items-center justify-end gap-50">
            <PrismButton
              @buttonType="ghost"
              @icon="search"
              @onClick={{fn this.toggleSearch true}}
              @size="small"
              data-event-id="hotel-revenue-insight-breakdown-search"
              data-event-value="subscription_id"
            />

            <LinkTo
              @query={{hash segmentationId="subscription_id"}}
              @route={{this.breakdownDetailRoute}}
              tabindex="-1"
            >
              <PrismButton
                @buttonType="ghost"
                @icon="expand"
                @size="small"
                data-event-id="hotel-revenue-insight-breakdown-expand"
                data-event-value="subscription_id"
              />
            </LinkTo>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
